import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut, Settings, ChevronDown, SwitchCamera, LifeBuoy } from 'lucide-react';
import { BRAND_NAME } from '../../config/brand';

interface AccountMenuProps {
  /** User display name */
  userName: string;
  /** User email address */
  userEmail: string;
  /** Sign out callback */
  onSignOut: () => void;
  /** Settings callback */
  onSettings?: () => void;
  /** Switch workspace callback or href */
  onSwitchWorkspace?: () => void;
  /** URL for switch workspace deep-link (alternative to onSwitchWorkspace) */
  switchWorkspaceHref?: string;
}

/**
 * AccountMenu — Dropdown menu for user account actions.
 *
 * Shows user avatar/name and provides access to settings and sign out.
 */
export default function AccountMenu({
  userName,
  userEmail,
  onSignOut,
  onSettings,
  onSwitchWorkspace,
  switchWorkspaceHref,
}: AccountMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="relative" ref={menuRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="
          flex items-center gap-2 px-2 py-1.5 rounded-md
          hover:bg-[var(--color-bg-hover)]
          transition-colors
        "
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Avatar — neutral, not orange (K3 design gate 2026-10-09): the accent budget is
            reserved for primary actions + the active-nav bar, not chrome identity. */}
        <div className="
          w-8 h-8 rounded-full
          bg-[var(--color-bg-elevated)] border border-[var(--color-border)]
          flex items-center justify-center
          text-[var(--color-text-primary)] text-sm font-semibold
        ">
          {initials}
        </div>

        {/* Name - hidden on small screens */}
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-[var(--color-text-primary)] truncate max-w-[120px]">
            {userName}
          </p>
        </div>

        <ChevronDown className={`
          w-4 h-4 text-[var(--color-text-muted)]
          transition-transform
          ${isOpen ? 'rotate-180' : ''}
        `} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="
          absolute right-0 mt-2 w-56
          bg-[var(--color-bg-surface)]
          border border-[var(--color-border)]
          rounded-lg shadow-lg
          z-50
        ">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-[var(--color-border-sub)]">
            <p className="text-sm font-medium text-[var(--color-text-primary)]">{userName}</p>
            <p className="text-xs text-[var(--color-text-muted)] truncate">{userEmail}</p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {(onSwitchWorkspace || switchWorkspaceHref) && (
              <button
                onClick={() => {
                  if (onSwitchWorkspace) {
                    onSwitchWorkspace();
                  } else if (switchWorkspaceHref) {
                    window.location.href = switchWorkspaceHref;
                  }
                  setIsOpen(false);
                }}
                className="
                  w-full flex items-center gap-3 px-4 py-2
                  text-sm text-[var(--color-text-secondary)]
                  hover:bg-[var(--color-bg-hover)]
                  transition-colors
                "
              >
                <SwitchCamera className="w-4 h-4" />
                Switch {BRAND_NAME} workspace
              </button>
            )}

            {onSettings && (
              <button
                onClick={() => {
                  onSettings();
                  setIsOpen(false);
                }}
                className="
                  w-full flex items-center gap-3 px-4 py-2
                  text-sm text-[var(--color-text-secondary)]
                  hover:bg-[var(--color-bg-hover)]
                  transition-colors
                "
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
            )}

            <button
              onClick={() => {
                window.location.assign('/my-reports');
                setIsOpen(false);
              }}
              className="
                w-full flex items-center gap-3 px-4 py-2
                text-sm text-[var(--color-text-secondary)]
                hover:bg-[var(--color-bg-hover)]
                transition-colors
              "
            >
              <LifeBuoy className="w-4 h-4" />
              My reports
            </button>

            <button
              onClick={() => {
                onSignOut();
                setIsOpen(false);
              }}
              className="
                w-full flex items-center gap-3 px-4 py-2
                text-sm text-[var(--color-text-secondary)]
                hover:bg-[var(--color-bg-hover)]
                transition-colors
              "
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
